import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faEye, faPlus } from '@fortawesome/free-solid-svg-icons'

import { DashboardLayout } from '../../Components/Layout/DashboardLayout'
import CustomTable from '../../Components/CustomTable'
import CustomButton from '../../Components/CustomButton'
import CustomModal from '../../Components/CustomModal'
import EditNeighborhoodModal from './EditNeighborhoodModal'
import { useGetNeighborhoodsQuery, useChangeNeighborhoodStatusMutation } from '../../Redux/Apis/Neighborhood'

import './style.css'

const NeighborhoodManagement = () => {
  const navigate = useNavigate()
  const { user } = useSelector(state => state.authSlice)

  const { data, isLoading, refetch } = useGetNeighborhoodsQuery()
  const [changeStatus] = useChangeNeighborhoodStatusMutation()

  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(null)
  const [modalAction, setModalAction] = useState('')

  const isSuperAdmin = user?.role === 'super_admin'

  const handleStatusChange = async (neighborhood, newStatus) => {
    setSelectedNeighborhood(neighborhood)
    setModalAction(newStatus)
    setShowStatusModal(true)
  }

  const handleEdit = (neighborhood) => {
    setSelectedNeighborhood(neighborhood)
    setShowEditModal(true)
  }

  const confirmStatusChange = async () => {
    try {
      const result = await changeStatus({
        id: selectedNeighborhood._id,
        status: modalAction
      }).unwrap()

      toast.success(`Neighborhood ${modalAction === 'active' ? 'activated' : modalAction === 'suspended' ? 'suspended' : 'set to maintenance'}`)
      refetch()
      setShowStatusModal(false)
    } catch (error) {
      console.error('Status change error:', error)
      toast.error(error?.data?.message || error?.message || 'Failed to change status')
    }
  }

  const handleEditSuccess = () => {
    refetch()
  }

  const getStatusBadge = (status) => {
    const colors = {
      active: 'success',
      maintenance: 'warning',
      suspended: 'danger'
    }
    return (
      <span className={`badge bg-${colors[status] || 'secondary'}`}>
        {status}
      </span>
    )
  }

  const headers = [
    {
      key: 'name',
      title: 'Neighborhood Name'
    },
    {
      key: 'domain',
      title: 'Domain'
    },
    {
      key: 'features',
      title: 'Features'
    },
    {
      key: 'status',
      title: 'Status'
    },
    {
      key: 'actions',
      title: 'Actions'
    }
  ]

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12">
            <div className="dashCard">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mainTitle">Neighborhood Management</h2>
                {isSuperAdmin && (
                  <CustomButton
                    variant="primary"
                    onClick={() => navigate('/neighborhoods/create')}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Neighborhood
                  </CustomButton>
                )}
              </div>

              {!isSuperAdmin && (
                <div className="alert alert-info mb-3">
                  You can view neighborhoods assigned to you. Contact a super admin to manage neighborhoods.
                </div>
              )}

              <CustomTable
                headers={headers}
                length={data?.data?.length || 0}
              >
                <tbody>
                  {(data?.data || []).map((row, index) => (
                    <tr key={row._id}>
                      <td>
                        <div className="fw-bold">{row.displayName}</div>
                        <small className="text-muted">{row.slug}</small>
                      </td>
                      <td>
                        <div>{row.domain}</div>
                        {row.apiUrl && <small className="text-muted d-block">{row.apiUrl}</small>}
                      </td>
                      <td>
                        <small className="d-block">{row.features?.sections?.length || 0} sections</small>
                        <small className="text-muted">
                          {row.features?.enableForums ? '✓ Forums ' : ''}
                          {row.features?.enableDirections ? '✓ Directions ' : ''}
                          {row.features?.enableChildTracking ? '✓ Tracking' : ''}
                        </small>
                      </td>
                      <td>{getStatusBadge(row.status)}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <CustomButton
                            variant="primaryButton"
                            text="View"
                            onClick={() => navigate(`/neighborhoods/view/${row._id}`)}
                          />

                          {isSuperAdmin && (
                            <>
                              <CustomButton
                                variant="warningButton"
                                text="Edit"
                                onClick={() => handleEdit(row)}
                              />

                              {row.status === 'active' && (
                                <>
                                  <CustomButton
                                    variant="secondaryButton"
                                    text="Maintenance"
                                    onClick={() => handleStatusChange(row, 'maintenance')}
                                  />
                                  <CustomButton
                                    variant="dangerButton"
                                    text="Suspend"
                                    onClick={() => handleStatusChange(row, 'suspended')}
                                  />
                                </>
                              )}

                              {row.status !== 'active' && (
                                <CustomButton
                                  variant="successButton"
                                  text="Activate"
                                  onClick={() => handleStatusChange(row, 'active')}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CustomTable>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        show={showStatusModal}
        close={() => setShowStatusModal(false)}
        action={confirmStatusChange}
        heading={`Change Neighborhood Status`}
      >
        <p>
          Are you sure you want to change <strong>{selectedNeighborhood?.name}</strong> status to{' '}
          <strong>{modalAction}</strong>?
        </p>
        {modalAction === 'suspended' && (
          <div className="alert alert-warning">
            Users in this neighborhood will not be able to access the app while suspended.
          </div>
        )}
        {modalAction === 'maintenance' && (
          <div className="alert alert-info">
            Users will see a maintenance message when trying to access this neighborhood.
          </div>
        )}
      </CustomModal>

      <EditNeighborhoodModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        neighborhood={selectedNeighborhood}
        onSuccess={handleEditSuccess}
      />
    </DashboardLayout>
  )
}

export default NeighborhoodManagement

